import {
  AiDocumentationFieldEnum,
  AiFieldDetectionStatusEnum,
  Documentation,
} from 'graphql-common';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';

// The per-field UI to render on the document view, derived from the stored
// `aiDetectionStatus` / `suggestedCodes` plus the field's own value and any
// local (client-only) generation/dismiss state. See the FE plan / BE guide §7.
export enum AiFieldUiState {
  // No AI affordance — render the field exactly as a non-AI document would.
  None = 'none',
  // Detection is in progress (QUEUED/DETECTING) or a manual Try-again is in
  // flight — show the "Generating…" placeholder.
  Detecting = 'detecting',
  // Detection failed and the field is still empty — show the failure affordance
  // (Cancel + Try again for description/tags; warning + edit for code).
  Failed = 'failed',
  // The code was detected but several candidates exist — show the value plus the
  // "Multiple matches found" warning + edit affordance.
  MultipleCodes = 'multipleCodes',
}

// Read the stored detection status for a single field (undefined when the field
// was never part of detection, e.g. a manually created document).
export function getDetectionStatus(
  doc: Pick<Documentation, 'aiDetectionStatus'> | null | undefined,
  field: AiDocumentationFieldEnum,
): AiFieldDetectionStatusEnum | undefined {
  return (doc?.aiDetectionStatus || []).find((entry) => entry.field === field)
    ?.status;
}

// Whether the field already holds a value worth rendering. A present value
// always wins over a stale status (so a saved Try-again result sticks even
// though the BE never clears `FAILED`).
//
// CATEGORY note: every document always has *some* category (the BE defaults
// to "Uncategorized" rather than leaving it null), so this can't distinguish
// "still the default" from "a real pick" the way the other fields' emptiness
// checks can. Treating any assigned category as "has a value" means the
// persisted Detecting/Failed popover won't surface for CATEGORY specifically
// — a narrow, accepted gap in the bulk-import status display, not the live
// per-field Generate button (gated separately by isAiAssistVisible).
export function getFieldHasValue(
  doc:
    | Pick<Documentation, 'name' | 'code' | 'description' | 'tags' | 'category'>
    | null
    | undefined,
  field: AiDocumentationFieldEnum,
): boolean {
  if (!doc) return false;
  switch (field) {
    case AiDocumentationFieldEnum.Name:
      return Boolean(doc.name);
    case AiDocumentationFieldEnum.Code:
      return Boolean(doc.code);
    case AiDocumentationFieldEnum.Description:
      return hasTextEditorValue(doc.description);
    case AiDocumentationFieldEnum.Tags:
      return (doc.tags || []).length > 0;
    case AiDocumentationFieldEnum.Category:
      return Boolean(doc.category);
    default:
      return false;
  }
}

type Args = {
  doc:
    | Pick<
        Documentation,
        | 'name'
        | 'code'
        | 'description'
        | 'tags'
        | 'category'
        | 'aiDetectionStatus'
        | 'suggestedCodes'
      >
    | null
    | undefined;
  field: AiDocumentationFieldEnum;
  // A manual Try-again generation is in flight for this field (client-only).
  isGenerating?: boolean;
  // The user dismissed this field's failure message (client-only).
  isDismissed?: boolean;
};

export type AiFieldDetectionState = {
  status: AiFieldDetectionStatusEnum | undefined;
  uiState: AiFieldUiState;
};

// Resolve the UI state for one field. Pure — all client-only state is passed in,
// so it is fully unit-testable. Ordering mirrors the FE plan's state machine.
export default function getAiFieldDetectionState({
  doc,
  field,
  isGenerating = false,
  isDismissed = false,
}: Args): AiFieldDetectionState {
  const status = getDetectionStatus(doc, field);
  const hasValue = getFieldHasValue(doc, field);

  // 1. A manual Try-again in flight always shows the placeholder.
  if (isGenerating) {
    return { status, uiState: AiFieldUiState.Detecting };
  }

  // 2. Code with several detected candidates → the multiple-matches affordance,
  // keyed off the candidate list (NOT the code value): the BE leaves `code`
  // null when it detects multiple codes (only `suggestedCodes` is filled), and
  // never clears `suggestedCodes` after the user picks one.
  if (
    field === AiDocumentationFieldEnum.Code &&
    (doc?.suggestedCodes?.length ?? 0) > 1
  ) {
    return { status, uiState: AiFieldUiState.MultipleCodes };
  }

  // 3. A present value wins over the stored status.
  if (hasValue) {
    return { status, uiState: AiFieldUiState.None };
  }

  // 4. Empty field — driven by the stored detection status.
  if (
    status === AiFieldDetectionStatusEnum.Queued ||
    status === AiFieldDetectionStatusEnum.Detecting
  ) {
    return { status, uiState: AiFieldUiState.Detecting };
  }
  // FAILED (detection errored) and CANCELLED (detection was stopped before it
  // reached this field) both leave the field empty with no result — surface the
  // same failure affordance (Retry/Dismiss) unless the user already dismissed it.
  if (
    (status === AiFieldDetectionStatusEnum.Failed ||
      status === AiFieldDetectionStatusEnum.Cancelled) &&
    !isDismissed
  ) {
    return { status, uiState: AiFieldUiState.Failed };
  }

  // DISMISSED (the user settled it via aiDismissDocumentationDetection),
  // NOT_FOUND / SKIPPED_EXISTING / UNSUPPORTED_FILE / DETECTED-but-empty /
  // absent → no affordance, render the field normally (empty).
  return { status, uiState: AiFieldUiState.None };
}
